import './CustomerInsightsChart.scss';
import cn from 'classnames';
import {
  Dispatch,
  ReactElement,
  SetStateAction,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { Cell, Legend, Pie, PieChart } from 'recharts';
import { formatDecimal, formatPercent } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { KnowYourCustomerData } from '@blisspointmedia/bpm-types/dist/KnowYourCustomer';
import { ChartContainer } from '@Components/ChartContainer';
import { InteractiveDiv } from '@Components/InteractiveEl';
import { useContainerSize } from '../../utils/hooks/useContainerSize';
import { getChannelSeriesColor, Neutral300 } from '../../utils/colors';
import { ChartContainerRightActions } from './ChartContainerRightActions';
import {
  ChartDataPoint,
  CUSTOMER_INSIGHTS_PRETTY_NAMES,
  CustomerInsightsFields
} from './customer-insights-constants';
import { buildColorMap } from './customer-insights-utils';
import { CustomerInsightsLegend } from './CustomerInsightsLegend';

const DEFAULT_BAR_CONTAINER_HEIGHT = 48;

interface DoubleBarProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: ReactElement;
}

const DoubleBar = ({
  data,
  fieldPrettyName,
  setSelectedValue,
  chartContainerRightActions
}: DoubleBarProps) => {
  const [hoveredValue, setHoveredValue] = useState<string | undefined>();
  const barMax = Math.max(
    ...data.map(val => val.clientPopulationFraction),
    ...data.map(val => val.tuPopulationFraction)
  );
  const colorMap = useMemo(() => buildColorMap(data), [data]);

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulationFraction, tuPopulationFraction }) => ({
        name: levels,
        val1: formatPercent(clientPopulationFraction, 1),
        val2: formatPercent(tuPopulationFraction, 1),
        computedValue: formatDecimal(clientPopulationFraction / tuPopulationFraction, 2)
      })),
    [data]
  );

  return (
    <div className='customer-insights-bar-chart index-bar-chart'>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        {data.map(({ levels, clientPopulationFraction, tuPopulationFraction }, index) => (
          <InteractiveDiv
            key={levels}
            className={cn('index-bar-container', {
              'hovered-value': levels === hoveredValue,
              'unhovered-value': Boolean(hoveredValue) && levels !== hoveredValue
            })}
            style={{ height: DEFAULT_BAR_CONTAINER_HEIGHT }}
            onClick={() => setSelectedValue(levels)}
            onMouseEnter={() => setHoveredValue(levels)}
            onMouseLeave={() => setHoveredValue(undefined)}
          >
            <CustomerInsightsLegend
              data={[legendFormattedData[index]]}
              hoveredValue={hoveredValue}
              setHoveredValue={setHoveredValue}
              setSelectedValue={setSelectedValue}
            />
            <div className='index-bars'>
              <div
                className='index-bar customer'
                style={{
                  width: `${(clientPopulationFraction / barMax) * 100}%`,
                  backgroundColor: colorMap[levels]
                }}
              />
              <div
                className='index-bar market'
                style={{
                  width: `${(tuPopulationFraction / barMax) * 100}%`,
                  backgroundColor: colorMap[levels]
                }}
              />
            </div>
          </InteractiveDiv>
        ))}
      </ChartContainer>
    </div>
  );
};

interface SelectedPieProps {
  data: KnowYourCustomerData[];
  fieldPrettyName: string;
  selectedValue: string;
  setSelectedValue: Dispatch<SetStateAction<string | undefined>>;
  chartContainerRightActions: ReactElement;
}

const SelectedPie = ({
  data,
  fieldPrettyName,
  selectedValue,
  setSelectedValue,
  chartContainerRightActions
}: SelectedPieProps) => {
  const pieRef = useRef<HTMLDivElement>(null);
  const sizeRef = useRef<HTMLDivElement>(null);
  const pieSize = useContainerSize(sizeRef);

  const colorMap = useMemo(() => {
    const map: Record<string, string> = {};
    for (let i = 0; i < data.length; i++) {
      const color = getChannelSeriesColor(i, true);
      map[data[i].levels] = color;
    }
    return map;
  }, [data]);

  const innerPieFormattedData = useMemo(() => {
    const formattedData: ChartDataPoint[] = [];

    const selectedData = data.find(({ levels }) => levels === selectedValue);
    if (selectedData) {
      formattedData.push({
        name: selectedData.levels,
        value: Math.round(selectedData.clientPopulation)
      });
      formattedData.push({
        name: 'Non-index',
        value: Math.round(selectedData.clientPopulation / selectedData.clientPopulationFraction)
      });
    }

    return formattedData;
  }, [data, selectedValue]);

  const outerPieFormattedData = useMemo(() => {
    const formattedData: ChartDataPoint[] = [];

    const selectedData = data.find(({ levels }) => levels === selectedValue);
    if (selectedData) {
      formattedData.push({
        name: selectedData.levels,
        value: Math.round(selectedData.tuPopulation)
      });
      formattedData.push({
        name: 'Non-index',
        value: Math.round(selectedData.tuPopulation / selectedData.tuPopulationFraction)
      });
    }

    return formattedData;
  }, [data, selectedValue]);

  const legendFormattedData = useMemo(
    () =>
      data.map(({ levels, clientPopulationFraction, tuPopulationFraction }) => ({
        name: levels,
        val1: formatPercent(clientPopulationFraction, 1),
        val2: formatPercent(tuPopulationFraction, 1),
        computedValue: formatDecimal(clientPopulationFraction / tuPopulationFraction, 2)
      })),
    [data]
  );

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!pieRef.current) {
        return;
      }

      const legend = pieRef.current.querySelector('.recharts-legend-wrapper .legend');
      const innerPie = pieRef.current.querySelector('.recharts-pie.index-inner-pie');
      const outerPie = pieRef.current.querySelector('.recharts-pie.index-outer-pie');

      const target = event.target as Node | null;
      const withinLegend = legend?.contains(target);
      const withinInnerPie = innerPie?.contains(target);
      const withinOuterPie = outerPie?.contains(target);

      if (!(withinLegend || withinInnerPie || withinOuterPie)) {
        setSelectedValue(undefined);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [setSelectedValue]);

  return (
    <div className='customer-insights-pie-chart index-selected-pie-chart' ref={pieRef}>
      <ChartContainer title={fieldPrettyName} rightActions={chartContainerRightActions}>
        <div ref={sizeRef} className='pie-sizer'>
          {pieSize && (
            <PieChart width={pieSize.width} height={pieSize.height}>
              <Legend
                content={
                  <CustomerInsightsLegend
                    data={legendFormattedData}
                    selectedValue={selectedValue}
                    setSelectedValue={setSelectedValue}
                  />
                }
                layout='vertical'
                align='left'
                verticalAlign='top'
              />
              <Pie
                className='index-inner-pie'
                data={innerPieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='15%'
                outerRadius='45%'
                dataKey='value'
                onClick={() => setSelectedValue(undefined)}
              >
                {innerPieFormattedData.map(({ name }) => (
                  <Cell
                    className='pie-cell'
                    key={`cell-${name}`}
                    fill={colorMap[name] ?? Neutral300}
                  />
                ))}
              </Pie>
              <Pie
                className='index-outer-pie'
                data={outerPieFormattedData}
                isAnimationActive={false}
                startAngle={90}
                endAngle={-270}
                cx='50%'
                cy='50%'
                innerRadius='46%'
                outerRadius='76%'
                dataKey='value'
                onClick={() => setSelectedValue(undefined)}
              >
                {outerPieFormattedData.map(({ name }) => (
                  <Cell
                    className='pie-cell'
                    key={`cell-${name}`}
                    fill={colorMap[name] ?? Neutral300}
                  />
                ))}
              </Pie>
            </PieChart>
          )}
        </div>
      </ChartContainer>
    </div>
  );
};

interface IndexChartProps {
  fieldName: CustomerInsightsFields;
  data: KnowYourCustomerData[];
}

export const IndexChart = ({ fieldName, data }: IndexChartProps) => {
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const fieldPrettyName = CUSTOMER_INSIGHTS_PRETTY_NAMES[fieldName];

  if (selectedValue) {
    return (
      <SelectedPie
        data={data}
        fieldPrettyName={fieldPrettyName}
        selectedValue={selectedValue}
        setSelectedValue={setSelectedValue}
        chartContainerRightActions={
          <ChartContainerRightActions fieldName={fieldName} data={data} />
        }
      />
    );
  }

  return (
    <DoubleBar
      data={data}
      fieldPrettyName={fieldPrettyName}
      setSelectedValue={setSelectedValue}
      chartContainerRightActions={<ChartContainerRightActions fieldName={fieldName} data={data} />}
    />
  );
};
